/**
 * Factory shaders: the vidOS default shaders, shared read-only into the
 * Shaders folder of Hypno 2 owners and shadygram subscribers. The server flags
 * every row of that tree (folders and files) with `factory: 1`.
 *
 * Rule for every client: PLAY ONLY, SOURCE HIDDEN. A Factory shader may be
 * played in the mixer; it is never downloaded, zipped, copied, renamed, moved,
 * deleted, shared, sold, published, opened in a code view or attached to a
 * chat, for everybody including the owning admin account. The server refuses
 * the same things (403 FACTORY_LOCKED / FACTORY_CODE); this module only lets
 * the UI offer the right actions and word the refusals.
 *
 * Note the source still reaches the browser when a shader is played (the GPU
 * has to compile it), so this is access control, not secrecy.
 */

/** What a code view shows in place of a Factory shader's source. */
export const FACTORY_SOURCE_HIDDEN_NOTE = 'Factory shader — source hidden';

/** Why a Factory row has no download/copy/share actions. */
export const FACTORY_PLAY_ONLY_MESSAGE =
  "Factory shaders are play-only: use them in the mixer. They can't be downloaded, copied, shared or added to a chat.";

/** Server error codes for Factory refusals. */
export const FACTORY_ERROR_CODE = Object.freeze({
  /** The caller is not entitled (no Hypno 2 on the account, no paid plan). */
  locked: 'FACTORY_LOCKED',
  /** The submitted code is a Factory shader (publish, fork, conversation save). */
  code: 'FACTORY_CODE',
});

const FACTORY_ERROR_MESSAGES: Readonly<Record<string, string>> = Object.freeze({
  [FACTORY_ERROR_CODE.locked]:
    'Factory shaders come with a Hypno 2 on your account or a shadygram subscription.',
  [FACTORY_ERROR_CODE.code]:
    "This is a Factory shader: it can be played in the mixer, but it can't be published, forked or saved as your own.",
});

/** Anything carrying the server's `factory` flag (a folder or file row, a drag payload). */
export interface FactoryFlagged {
  factory?: number | boolean | null;
}

/** True when the row belongs to the Factory tree. */
export function isFactoryRow(row: FactoryFlagged | null | undefined): boolean {
  return !!row?.factory;
}

/**
 * True when the viewer may not change the row: someone else's shared or
 * purchased tree (`shared_readonly`) or the Factory tree (also read-only for
 * its owner — the server re-seeds it from the shipped copy).
 */
export function isReadOnlyRow(
  row: (FactoryFlagged & { shared_readonly?: number | boolean | null }) | null | undefined,
): boolean {
  return !!row?.shared_readonly || isFactoryRow(row);
}

/**
 * The user-facing message for a Factory refusal, given a parsed error body
 * ({ code, error }); null when the body is not a Factory refusal.
 */
export function factoryErrorMessage(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null;
  const code = (body as { code?: unknown }).code;
  if (typeof code !== 'string') return null;
  return FACTORY_ERROR_MESSAGES[code] ?? null;
}

/** True when a raw `application/x-library-media` drag payload is a Factory row. */
export function isFactoryDragPayload(raw: string | null | undefined): boolean {
  if (!raw) return false;
  try {
    return isFactoryRow(JSON.parse(raw) as FactoryFlagged);
  } catch {
    return false;
  }
}
